'use client';

import { useState, useEffect, useRef, use } from 'react';
import Link from 'next/link';
import ShareButtons from '@/app/components/ShareButtons';
import RepCard from '@/app/components/RepCard';
import CopyLinkBox from '@/app/components/CopyLinkBox';
import { useRepFlow, MAX_CHARS } from '@/app/components/useRepFlow';
import { getRepId, getRepFullName, US_STATES } from '@/app/lib/reps';
import { getTotalPrice, getFormattedTotal, getPerCardAverage, getMarginalCost } from '@/app/lib/pricing';

type StepStr = 'search' | 'select';

export default function SharedMessagePage({ params }: { params: Promise<{ id: string }> }) {
  const resolvedParams = use(params);
  const messageId = resolvedParams.id;

  const [step, setStep] = useState<StepStr>('search');
  const [pageLoading, setPageLoading] = useState(true);
  const urlPromoRef = useRef<string | null>(null); // captured before replaceState strips it
  const vsSlugARef = useRef<string | null>(null);
  const vsSlugBRef = useRef<string | null>(null);

  // Track both the original message and the current edits
  const [originalMessage, setOriginalMessage] = useState('');
  const [slug, setSlug] = useState<string | null>(null);
  const [sendCount, setSendCount] = useState(0);

  const restoredStepRef = useRef<StepStr | null>(null);
  // Set when a saved draft supplied a message, so the campaign fetch below
  // doesn't overwrite the user's own words with the original campaign text.
  const restoredMessageRef = useRef(false);
  const flow = useRepFlow({
    draftKey: `ptc_draft:m:${messageId}`,
    // Persist the message. This page lets people personalize a campaign (see the
    // "You've personalized this message!" banner), and previously that edit was
    // never saved — so a reload showed "we saved your progress" while silently
    // reverting the text to the campaign original, and checkout then mailed
    // words the sender never wrote.
    persistMessage: true,
    draftExtras: { step },
    onDraftRestored: (draft) => {
      if (typeof draft.message === 'string' && draft.message.trim()) {
        restoredMessageRef.current = true;
      }
      if (draft.step === 'select') {
        restoredStepRef.current = 'select';
        setStep('select');
      }
    },
  });
  const {
    loading, searchMode, setSearchMode, address, setAddress, repName, setRepName,
    cart, searchResults,
    message, setMessage, isCheckingOut,
    promoCode, setPromoCode, appliedPromo, setAppliedPromo, promoError, setPromoError,
    showPromoInput, setShowPromoInput, promoLoading,
    okToContact, setOkToContact, subscribeNewsletter, setSubscribeNewsletter,
    govLevels, setGovLevels, stateJurisdiction, setStateJurisdiction,
    searchError, searchNotice, checkoutError,
    cartIds, cartTotal,
    runSearch, addToCart, addAllToCart, removeFromCart,
    handleMessageChange, handleApplyPromo, applyUrlPromo, handleCheckout,
    draftRestored, discardDraft,
  } = flow;

  const startFresh = () => {
    discardDraft();
    // discardDraft clears the message; on a campaign page "start fresh" means
    // back to the campaign's own text, not an empty postcard.
    restoredMessageRef.current = false;
    if (originalMessage) setMessage(originalMessage);
    setStep('search');
    window.history.replaceState({ step: 'search' }, '', '#search');
  };

  // ==========================================
  // BROWSER BACK BUTTON SUPPORT
  // ==========================================
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const modeParam = params.get('mode');
    urlPromoRef.current = params.get('promo'); // capture before replaceState strips it
    vsSlugARef.current = params.get('vs_a');
    vsSlugBRef.current = params.get('vs_b');
    if (modeParam === 'name') setSearchMode('name');
    if (restoredStepRef.current) {
      // A saved draft restored us to the select step — keep history in sync
      window.history.replaceState({ step: restoredStepRef.current }, '', `#${restoredStepRef.current}`);
    } else {
      window.history.replaceState(
        { step: 'search' }, '',
        modeParam === 'name' ? '?mode=name#search' : '?mode=address#search'
      );
    }

    const handlePopState = (event: PopStateEvent) => {
      if (event.state && event.state.step) {
        setStep(event.state.step as StepStr);
      } else {
        setStep('search');
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goToStep = (newStep: StepStr) => {
    setStep(newStep);
    const prefix = searchMode === 'name' ? '?mode=name' : '?mode=address';
    window.history.pushState({ step: newStep }, '', `${prefix}#${newStep}`);
  };

  useEffect(() => {
    const fetchMessage = async () => {
      try {
        const res = await fetch(`/api/messages/${messageId}`);
        if (res.ok) {
          const data = await res.json();
          // originalMessage always comes from the server — it's what "Reset" and
          // the isEdited badge compare against. But only seed the editable text
          // when a draft didn't already restore the sender's personalized copy.
          setOriginalMessage(data.content);
          if (!restoredMessageRef.current) setMessage(data.content);
          setSendCount(data.send_count);
          setSlug(data.slug || null);
        } else {
          setMessage("Message not found. It may have been removed.");
        }
      } catch (error) {
        setMessage("Error loading message.");
      } finally {
        setPageLoading(false);
        // Auto-apply URL promo after page is fully loaded.
        // Read from ref (captured in back-button effect before replaceState stripped it).
        if (urlPromoRef.current) applyUrlPromo(urlPromoRef.current);
      }
    };
    fetchMessage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageId]);

  // ==========================================
  // SEARCH / CHECKOUT (page-specific wrappers)
  // ==========================================
  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    const legislators = await runSearch();
    if (legislators && legislators.length > 0) {
      addAllToCart(legislators);
      goToStep('select');
    }
  };

  const handleSearchAgain = async (e: React.FormEvent) => {
    e.preventDefault();
    await runSearch();
    // Stay on select step — results replace the top zone, cart unchanged
  };

  const startCheckout = () =>
    handleCheckout({ vsSlugA: vsSlugARef.current, vsSlugB: vsSlugBRef.current });

  if (pageLoading && !message) {
    return <div className="min-h-screen flex items-center justify-center text-white">Loading campaign...</div>;
  }

  const isEdited = originalMessage && message !== originalMessage;

  const shareUrl = `https://postcardstocongress.org/m/${slug || messageId}`;

  const newResultsNotInCart = searchResults.filter(
    (r: any) => !cartIds.includes(getRepId(r))
  );

  return (
    <main className="min-h-screen flex flex-col items-center p-4 sm:p-6">

      {/* --- RESTORED DRAFT BANNER --- */}
      {draftRestored && (
        <div className="w-full max-w-2xl mt-4 px-4 py-3 bg-blue-900/30 border border-blue-500/40 rounded-xl text-blue-200 text-sm flex items-center justify-between gap-3 z-10 animate-in fade-in duration-300">
          <span>👋 Welcome back — we saved your progress from earlier.</span>
          <button
            onClick={startFresh}
            className="shrink-0 text-xs font-bold px-3 py-1.5 rounded-lg bg-blue-500/10 text-blue-300 hover:bg-blue-500/20 border border-blue-500/30 transition-colors"
          >
            Start fresh
          </button>
        </div>
      )}

      {/* STEP 1: READ/EDIT MESSAGE & SEARCH */}
      {step === 'search' && (
        <div className="max-w-6xl w-full mt-8 sm:mt-12 animate-in fade-in z-10 px-4">

          <div className="text-center mb-8">
            <h1 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight mb-2">Send this <span className="bg-gradient-to-r from-red-500 to-amber-400 bg-clip-text text-transparent">message</span> to your <span className="bg-gradient-to-r from-blue-400 to-red-400 bg-clip-text text-transparent">reps</span></h1>
            {!isEdited ? (
              <p className="text-lg text-slate-300 font-medium">This campaign has already been sent to Congress <span className="text-red-500 font-bold">{sendCount} times</span>.</p>
            ) : (
              <p className="text-lg text-amber-400 font-medium">You are creating a brand new personalized campaign.</p>
            )}
            <p className="mt-2">
              <Link href={`/vs?a=${messageId}`} className="text-xs text-slate-500 hover:text-amber-400 transition-colors">
                ⚔️ Compare with another campaign →
              </Link>
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-stretch">

            {/* LEFT SIDE: MESSAGE EDITOR & ADDRESS */}
            <div className="bg-white p-6 sm:p-8 rounded-3xl shadow-2xl border border-slate-100 flex flex-col h-full">

              {isEdited && (
                <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-xl mb-6 text-sm flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
                  <div>
                    ✏️ <strong>You&apos;ve personalized this message!</strong> You will get a fresh shareable link and your own sender counter after checkout.
                  </div>
                  <button
                    onClick={() => setMessage(originalMessage)}
                    className="px-4 py-2 bg-amber-200 hover:bg-amber-300 text-amber-900 rounded-lg text-xs font-bold transition-colors shadow-sm whitespace-nowrap active:scale-95"
                  >
                    ↺ Reset
                  </button>
                </div>
              )}

              <div className="bg-slate-50 border-2 border-slate-200 focus-within:border-blue-500 focus-within:ring-4 focus-within:ring-blue-500/20 rounded-2xl p-5 mb-8 transition-all flex flex-col">
                <p className="font-bold mb-2 text-slate-900 font-serif">Dear Representative(s),</p>
                <textarea
                  value={message}
                  onChange={handleMessageChange}
                  className="w-full bg-transparent text-slate-900 text-lg font-serif leading-relaxed resize-y min-h-[150px] outline-none"
                  placeholder="Write your message here..."
                  required
                />
                <p className="mt-2 text-slate-900 font-serif">Sincerely,<br/><span className="italic text-slate-500">[Your Name - Added at Checkout]</span></p>

                <div className="flex justify-between mt-4 text-sm font-medium border-t border-slate-200 pt-3">
                  <p className="text-slate-400">Keep it concise.</p>
                  <p className={message.length > MAX_CHARS * 0.9 ? "text-red-600 font-bold" : "text-slate-400"}>
                    {message.length} / {MAX_CHARS}
                  </p>
                </div>
              </div>

              {/* SHARE / CAMPAIGN LINK */}
              {!isEdited ? (
                <div className="mb-4">
                  <CopyLinkBox url={shareUrl} variant="dark" umamiEvent="campaign_link_copied" />
                </div>
              ) : (
                <div className="flex items-center gap-2 bg-[#07111e] border border-slate-700/50 rounded-xl px-4 py-3 mb-4">
                  <span className="text-slate-500 text-sm shrink-0">✏️</span>
                  <p className="text-slate-500 text-xs">
                    You&apos;ve customized this message. Send the first postcard to start your own campaign.
                  </p>
                </div>
              )}

              {/* Level + Find By toggles */}
              <div className="rounded-xl overflow-hidden border border-slate-200 mb-3 mt-auto">
                <div role="radiogroup" aria-label="Level of government">
                  <div className="w-full text-center text-xs font-bold uppercase tracking-widest py-1.5 bg-slate-100 text-slate-500">
                    Level
                  </div>
                  <div className="flex gap-1 bg-slate-50 p-1 text-sm font-semibold">
                    {(['federal', 'state'] as const).map(lvl => {
                      const isActive = govLevels.has(lvl);
                      return (
                        <button
                          key={lvl}
                          type="button"
                          role="radio"
                          aria-checked={isActive}
                          onClick={() => setGovLevels(new Set([lvl]))}
                          className={`flex-1 py-1.5 rounded-lg transition-all cursor-pointer ${
                            isActive
                              ? lvl === 'federal' ? 'bg-red-700 text-white shadow-sm' : 'bg-amber-600 text-white shadow-sm'
                              : 'text-slate-500 hover:text-slate-700'
                          }`}
                        >
                          {lvl === 'federal' ? 'Federal' : 'State'}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="border-t border-slate-200" />
                <div role="radiogroup" aria-label="Search method">
                  <div className="w-full text-center text-xs font-bold uppercase tracking-widest py-1.5 bg-slate-100 text-slate-500">
                    Find By
                  </div>
                  <div className="flex gap-1 bg-slate-50 p-1 text-sm font-semibold">
                    <button
                      type="button"
                      role="radio"
                      aria-checked={searchMode === 'address'}
                      onClick={() => { setSearchMode('address'); window.history.replaceState(null, '', '?mode=address#search'); }}
                      className={`flex-1 py-1.5 rounded-lg transition-all ${searchMode === 'address' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                    >
                      By Address
                    </button>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={searchMode === 'name'}
                      onClick={() => { setSearchMode('name'); window.history.replaceState(null, '', '?mode=name#search'); }}
                      className={`flex-1 py-1.5 rounded-lg transition-all ${searchMode === 'name' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                    >
                      By Name
                    </button>
                  </div>
                </div>
              </div>

              {searchError && (
                <div role="alert" className="mb-3 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm font-medium animate-in fade-in duration-300">
                  {searchError}
                </div>
              )}
              <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3">
                {searchMode === 'address' ? (
                  <input
                    type="text"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    placeholder="Your street address..."
                    required
                    className="flex-1 px-5 py-4 rounded-xl border border-slate-200 focus:ring-4 outline-none text-lg text-slate-900"
                  />
                ) : (
                  <div className="flex flex-col sm:flex-row gap-2 flex-1">
                    {govLevels.has('state') && (
                      <select
                        value={stateJurisdiction}
                        onChange={e => setStateJurisdiction(e.target.value)}
                        required={govLevels.has('state')}
                        className="px-4 py-4 rounded-xl border border-slate-200 focus:ring-4 outline-none text-base text-slate-900 bg-white"
                      >
                        <option value="">Select state…</option>
                        {US_STATES.map(([abbr, label]) => (
                          <option key={abbr} value={abbr}>{label}</option>
                        ))}
                      </select>
                    )}
                    <input
                      type="text"
                      value={repName}
                      onChange={(e) => setRepName(e.target.value)}
                      placeholder="e.g. Bernard Sanders, Ted Cruz..."
                      required
                      className="flex-1 px-5 py-4 rounded-xl border border-slate-200 focus:ring-4 outline-none text-lg text-slate-900"
                    />
                  </div>
                )}
                <button
                  type="submit"
                  disabled={loading || !message.trim() || (searchMode === 'address' ? !address.trim() : !repName.trim())}
                  className="px-8 py-4 bg-red-700 hover:bg-red-800 disabled:bg-slate-300 disabled:text-slate-500 text-white font-bold rounded-xl text-lg shadow-md transition-colors whitespace-nowrap active:scale-95"
                >
                  {loading ? 'Searching...' : 'Find My Reps'}
                </button>
              </form>
              <p className="text-xs text-slate-500 mt-4 text-center">
                By using this site, you agree to our <Link href="/terms" className="underline hover:text-slate-400">Terms of Service</Link>.
                {' '}Your billing address on file with your payment method will be used as the return address on the postcard — please ensure it is current.
              </p>
            </div>

            {/* RIGHT SIDE: LIVE PREVIEW */}
            <div className="sticky top-6 lg:top-10 flex flex-col">
              <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-4 text-center lg:text-left">Live Preview (Back of Card)</h3>

              <div className="aspect-[6/4] bg-[#fdfbf7] rounded-sm shadow-2xl shadow-black/50 border border-slate-200 pt-2 pb-4 px-4 sm:pt-3 sm:pb-6 sm:px-6 relative overflow-hidden"
                   style={{ backgroundImage: 'url("https://www.transparenttextures.com/patterns/cream-paper.png")' }}>
                <div className="absolute left-[58%] top-[10%] bottom-[10%] w-px bg-amber-300/50"></div>
                <div className="absolute top-2 right-4 sm:top-3 sm:right-6 w-14 h-16 sm:w-16 sm:h-20 border-2 border-amber-300/60 flex items-center justify-center text-[10px] sm:text-xs text-slate-400 font-bold tracking-tighter text-center bg-white/50 p-1">
                  POSTAGE REQUIRED
                </div>

                <div className="flex h-full">
                   <div className="w-[55%] pr-4 sm:pr-6 flex flex-col">
                     <div className="font-sans text-slate-800 text-sm sm:text-base leading-relaxed whitespace-pre-wrap flex flex-col h-full">
                       <p className="font-bold mb-2">Dear Representative(s),</p>
                       <div className="flex-grow break-words">
                         {message || <span className="text-slate-300 italic">Your message will appear here...</span>}
                       </div>
                       <p className="mt-2">
                         Sincerely,<br/>
                         <span className="italic text-slate-500 font-medium">[Your Name]</span>
                       </p>
                     </div>
                   </div>

                   <div className="w-[45%] pl-4 sm:pl-8 flex items-center">
                      <div className="text-slate-800 font-mono text-[10px] sm:text-xs md:text-sm uppercase leading-loose w-full">
                        <p className="font-bold text-slate-900 border-b border-slate-200 pb-2 mb-2">Your Representative</p>
                        <p>U.S. House / Senate</p>
                        <p>Washington, DC</p>
                        <p className="mt-6 text-[9px] sm:text-[10px] text-slate-500 normal-case leading-tight">
                          From: Constituent<br/>
                          {address ? (address.length > 35 ? address.substring(0, 35) + '...' : address) : 'Your Address...'}
                        </p>
                      </div>
                   </div>
                </div>
              </div>

              {!isEdited && (
                <ShareButtons
                  url={shareUrl}
                  text="I sent a postcard to Congress. Add your voice:"
                  showCard
                  className="mt-auto pt-4"
                />
              )}
            </div>

          </div>
        </div>
      )}

      {/* STEP 2: SELECT REPS & CHECKOUT */}
      {step === 'select' && (
        <div className="max-w-6xl w-full mt-8 sm:mt-10 animate-in fade-in slide-in-from-bottom-8 duration-500 z-10 px-4">

          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-white">Build your list.</h2>
            <p className="text-lg text-slate-300 mt-2">Search as many times as you like — add any rep to your list.</p>
          </div>

          {searchError && (
            <div role="alert" className="mb-4 px-4 py-3 bg-red-900/30 border border-red-500/40 rounded-xl text-red-200 text-sm font-medium animate-in fade-in duration-300">
              {searchError}
            </div>
          )}
          {searchNotice && (
            <div className="mb-4 px-4 py-3 bg-amber-900/30 border border-amber-500/40 rounded-xl text-amber-200 text-sm font-medium animate-in fade-in duration-300">
              {searchNotice}
            </div>
          )}

          {/* Search Again bar */}
          <div className="bg-[#fafaf7] rounded-2xl border border-amber-200/60 shadow-lg p-4 mb-8">
            <div className="rounded-xl overflow-hidden border border-slate-200 mb-3">
              <div role="radiogroup" aria-label="Level of government">
                <div className="flex gap-1 bg-slate-100 p-1 text-sm font-semibold">
                  {(['federal', 'state'] as const).map(lvl => {
                    const isActive = govLevels.has(lvl);
                    return (
                      <button
                        key={lvl}
                        type="button"
                        role="radio"
                        aria-checked={isActive}
                        onClick={() => setGovLevels(new Set([lvl]))}
                        className={`flex-1 py-1.5 rounded-lg transition-all cursor-pointer ${
                          isActive
                            ? lvl === 'federal' ? 'bg-red-700 text-white shadow-sm' : 'bg-amber-600 text-white shadow-sm'
                            : 'text-slate-500 hover:text-slate-700'
                        }`}
                      >
                        {lvl === 'federal' ? 'Federal' : 'State'}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="border-t border-slate-200" />
              <div role="radiogroup" aria-label="Search method">
                <div className="flex gap-1 bg-slate-100 p-1 text-sm font-semibold">
                  <button
                    type="button"
                    role="radio"
                    aria-checked={searchMode === 'address'}
                    onClick={() => { setSearchMode('address'); window.history.replaceState(null, '', '?mode=address#select'); }}
                    className={`flex-1 py-1.5 rounded-lg transition-all ${searchMode === 'address' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                  >
                    By Address
                  </button>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={searchMode === 'name'}
                    onClick={() => { setSearchMode('name'); window.history.replaceState(null, '', '?mode=name#select'); }}
                    className={`flex-1 py-1.5 rounded-lg transition-all ${searchMode === 'name' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                  >
                    By Name
                  </button>
                </div>
              </div>
            </div>
            <form onSubmit={handleSearchAgain} className="flex gap-2">
              {searchMode === 'name' && govLevels.has('state') && (
                <select
                  value={stateJurisdiction}
                  onChange={e => setStateJurisdiction(e.target.value)}
                  required
                  className="px-3 py-3 rounded-xl border border-slate-200 focus:ring-2 outline-none text-sm text-slate-900 bg-white"
                >
                  <option value="">State…</option>
                  {US_STATES.map(([abbr, label]) => (
                    <option key={abbr} value={abbr}>{label}</option>
                  ))}
                </select>
              )}
              <input
                type="text"
                value={searchMode === 'address' ? address : repName}
                onChange={(e) => searchMode === 'address' ? setAddress(e.target.value) : setRepName(e.target.value)}
                placeholder={searchMode === 'address' ? 'Search by address...' : 'Search by name...'}
                required
                className="flex-1 px-4 py-3 rounded-xl border border-slate-200 focus:ring-4 outline-none text-base text-slate-900"
              />
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-3 bg-slate-800 hover:bg-slate-700 disabled:bg-slate-300 text-white font-bold rounded-xl text-base transition-colors whitespace-nowrap active:scale-95"
              >
                {loading ? 'Searching...' : 'Search'}
              </button>
            </form>
          </div>

          {/* Search Results */}
          {searchResults.length > 0 && (
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider">Search Results</h3>
                {newResultsNotInCart.length > 0 && (
                  <button
                    onClick={() => addAllToCart(searchResults)}
                    className="text-sm font-bold text-amber-600 hover:text-amber-500 transition-colors"
                  >
                    Add All →
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {searchResults.map((rep, i) => (
                  <RepCard
                    key={getRepId(rep) || `r-${i}`}
                    rep={rep}
                    variant="result"
                    inCart={cartIds.includes(getRepId(rep) || `r-${i}`)}
                    onAdd={addToCart}
                    fallbackId={`r-${i}`}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Your List */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider">Your List ({cart.length})</h3>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-900/40 border border-amber-500/30 rounded-full text-amber-200 text-xs font-medium">
                <span>🏷️</span>
                <span>${(getPerCardAverage(cart.length) / 100).toFixed(2)}/card</span>
                {cart.length >= 3 && <span className="text-green-400">· Discount!</span>}
              </div>
            </div>

            {cart.length >= 1 && cart.length < 8 && newResultsNotInCart.length > 0 && (
              <div className="mb-4 px-4 py-3 bg-blue-900/20 border border-blue-500/30 rounded-xl text-blue-200 text-sm">
                Add 1 more for just <strong>${(getMarginalCost(cart.length) / 100).toFixed(2)}</strong> →{' '}
                {cart.length + 1} cards for <strong>${(getTotalPrice(cart.length + 1) / 100).toFixed(2)}</strong>
              </div>
            )}
            {cart.length === 8 && (
              <div className="mb-4 px-4 py-3 bg-green-900/20 border border-green-500/30 rounded-xl text-green-300 text-sm font-semibold">
                Maximum volume discount unlocked! $1.65 per additional card.
              </div>
            )}

            {cart.length > 10 && (
              <div className="mb-4 px-4 py-3 bg-amber-900/40 border border-amber-500/40 rounded-xl text-amber-200 text-sm font-medium">
                ⚠️ That&apos;s {cart.length} postcards · {getFormattedTotal(cart.length)} total — are you sure?
              </div>
            )}

            {cart.length === 0 ? (
              <div className="text-slate-400 text-center py-8 rounded-2xl border-2 border-dashed border-slate-700">
                Add representatives above to build your list.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {cart.map((rep, i) => (
                  <RepCard
                    key={getRepId(rep) || `c-${i}`}
                    rep={rep}
                    variant="cart"
                    onRemove={removeFromCart}
                    fallbackId={`c-${i}`}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Promo code */}
          <div className="mt-4 mb-3">
            {!showPromoInput ? (
              <button
                type="button"
                onClick={() => setShowPromoInput(true)}
                className="w-full py-3 rounded-xl border border-slate-600 text-slate-400 hover:border-slate-500 hover:text-slate-300 text-sm font-medium transition-colors"
              >
                🏷️ Have a promo code?
              </button>
            ) : (
              <div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={promoCode}
                    autoFocus
                    onChange={(e) => { setPromoCode(e.target.value); setAppliedPromo(null); setPromoError(''); }}
                    onKeyDown={(e) => e.key === 'Enter' && handleApplyPromo()}
                    placeholder="Enter promo code"
                    className="flex-1 px-4 py-3 rounded-xl border border-slate-600 bg-slate-800 text-white focus:ring-2 focus:ring-amber-400 outline-none text-sm"
                  />
                  <button
                    type="button"
                    onClick={handleApplyPromo}
                    disabled={promoLoading || !promoCode.trim()}
                    className="px-5 py-3 bg-slate-600 hover:bg-slate-500 disabled:bg-slate-700 text-white text-sm font-bold rounded-xl transition-colors whitespace-nowrap"
                  >
                    {promoLoading ? '...' : 'Apply'}
                  </button>
                </div>
                {appliedPromo && (
                  <p className="mt-2 text-sm text-green-400 font-semibold">✓ {appliedPromo.label} applied!</p>
                )}
                {promoError && (
                  <p className="mt-2 text-sm text-red-400 font-medium">✗ {promoError}</p>
                )}
              </div>
            )}
          </div>

          {/* Consent checkboxes */}
          <div className="flex flex-col gap-2 mt-4 text-sm text-slate-400">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input type="checkbox" checked={okToContact} onChange={e => setOkToContact(e.target.checked)} className="w-4 h-4 rounded accent-red-700" />
              It&apos;s okay for us to contact you via email.
            </label>
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input type="checkbox" checked={subscribeNewsletter} onChange={e => setSubscribeNewsletter(e.target.checked)} className="w-4 h-4 rounded accent-red-700" />
              Subscribe to our newsletter.
            </label>
          </div>

          {checkoutError && (
            <div role="alert" className="mt-4 px-4 py-3 bg-red-900/30 border border-red-500/40 rounded-xl text-red-200 text-sm font-medium animate-in fade-in duration-300">
              {checkoutError}
            </div>
          )}

          {/* Bottom Actions */}
          <div className="flex flex-col sm:flex-row items-center gap-4 mt-4">
            <button
              onClick={() => window.history.back()}
              className="px-6 py-3 text-slate-400 hover:text-white font-semibold rounded-xl transition-colors text-base"
            >
              ← Back
            </button>

            <div className="relative flex-1">
              {cart.length >= 3 && (
                <div className="absolute -top-3 -right-3 bg-green-500 text-white text-[10px] uppercase tracking-wider font-extrabold px-3 py-1 rounded-full shadow-lg transform rotate-6 z-20 animate-in zoom-in duration-300">
                  Discount Applied!
                </div>
              )}
              <button
                onClick={startCheckout}
                disabled={cart.length === 0 || isCheckingOut}
                className="w-full px-10 py-4 bg-red-700 hover:bg-red-800 disabled:bg-slate-600 disabled:text-slate-400 text-white font-bold rounded-xl text-lg sm:text-xl transition-all shadow-lg hover:shadow-red-800/40 active:scale-95"
              >
                {isCheckingOut
                  ? 'Loading Secure Checkout...'
                  : `Checkout (${cart.length} ${cart.length === 1 ? 'rep' : 'reps'}) · $${(cartTotal(cart.length) / 100).toFixed(2)} →`
                }
              </button>
            </div>
          </div>

          <p className="text-xs text-green-400 mt-4 text-center font-semibold">
            🔒 You&apos;re only charged after your postcards are accepted by the print facility — if printing fails, the hold on your card is released automatically.
          </p>
          <p className="text-xs text-slate-500 mt-2 text-center">
            By using this site, you agree to our <Link href="/terms" className="underline hover:text-slate-400">Terms of Service</Link>.
            {' '}Your billing address on file with your payment method will be used as the return address on the postcard — please ensure it is current.
          </p>

          {/* Live Preview */}
          {cart.length > 0 && (
            <div className="mt-10 max-w-2xl mx-auto w-full">
              <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-4 text-center">Live Preview (Back of Card)</h3>

              <div className="aspect-[6/4] bg-[#fdfbf7] rounded-sm shadow-2xl shadow-black/50 border border-slate-200 p-4 sm:p-6 relative overflow-hidden"
                   style={{ backgroundImage: 'url("https://www.transparenttextures.com/patterns/cream-paper.png")' }}>
                <div className="absolute left-[58%] top-[10%] bottom-[10%] w-px bg-amber-300/50"></div>
                <div className="absolute top-4 right-4 sm:top-6 sm:right-6 w-14 h-16 sm:w-16 sm:h-20 border-2 border-amber-300/60 flex items-center justify-center text-[10px] sm:text-xs text-slate-400 font-bold tracking-tighter text-center bg-white/50 p-1">
                  POSTAGE REQUIRED
                </div>

                <div className="flex h-full">
                  <div className="w-[55%] pr-4 sm:pr-6 pt-2 sm:pt-4 flex flex-col overflow-hidden">
                    <div className="font-sans text-slate-800 text-sm sm:text-base leading-relaxed whitespace-pre-wrap flex flex-col h-full">
                      <p className="font-bold mb-2">Dear Representative(s),</p>
                      <div className="flex-grow break-words overflow-hidden">
                        {message || <span className="text-slate-300 italic">Your message will appear here...</span>}
                      </div>
                      <p className="mt-2">
                        Sincerely,<br/>
                        <span className="italic text-slate-500 font-medium">[Your Name]</span>
                      </p>
                    </div>
                  </div>

                  <div className="w-[45%] pl-4 sm:pl-8 flex items-center">
                    {(() => {
                      const previewRep = cart[0];
                      const previewName = previewRep ? getRepFullName(previewRep) : 'Your Representative';
                      const isState = previewRep?.level === 'state';
                      const addr = isState ? previewRep.capitol_address : null;
                      const addrLine1 = addr?.line1 || (previewRep?.type === 'senator' ? 'U.S. Senate' : 'U.S. House of Representatives');
                      const addrCity = addr ? `${addr.city}, ${addr.state} ${addr.zip}` : `Washington, DC ${previewRep?.type === 'senator' ? '20510' : '20515'}`;
                      return (
                        <div className="text-slate-800 font-mono text-[10px] sm:text-xs md:text-sm uppercase leading-loose w-full pr-16 sm:pr-20">
                          <p className="font-bold text-slate-900 border-b border-slate-200 pb-2 mb-2">{previewName}</p>
                          <p>{addrLine1}</p>
                          <p>{addrCity}</p>
                          <p className="mt-6 text-[9px] sm:text-[10px] text-slate-500 normal-case leading-tight">
                            From: Constituent<br/>
                            {address ? (address.length > 35 ? address.substring(0, 35) + '...' : address) : 'Your Address...'}
                          </p>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>
          )}

        </div>
      )}
    </main>
  );
}
